(function () {
    const landingPage = document.querySelector('[data-page-view="landing"]');
    if (!(landingPage instanceof HTMLElement)) {
        return;
    }

    const hero = landingPage.querySelector('[data-landing-hero]');
    const heroContent = landingPage.querySelector('[data-landing-hero-content]');
    const ghostDashboard = landingPage.querySelector('[data-landing-ghost]');
    const ghostShell = landingPage.querySelector('[data-landing-ghost-shell]');
    const ctaButtons = Array.from(landingPage.querySelectorAll('[data-landing-cta]'));
    const revealItems = Array.from(landingPage.querySelectorAll('[data-landing-reveal]'));
    const showcaseSection = landingPage.querySelector('[data-landing-showcase-section]');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delayedItems = Array.from(landingPage.querySelectorAll('[data-landing-delay]'));
    const glowItems = Array.from(landingPage.querySelectorAll('[data-landing-glow]'));
    const showcaseCardContainers = Array.from(landingPage.querySelectorAll('[data-landing-showcase-card-container]'));

    delayedItems.forEach((item) => {
        if (!(item instanceof HTMLElement)) {
            return;
        }

        const rawDelay = item.dataset.landingDelay;
        const parsedDelay = Number.parseInt(String(rawDelay ?? ''), 10);
        if (!Number.isFinite(parsedDelay) || parsedDelay < 0) {
            return;
        }

        item.style.setProperty('--landing-reveal-delay', `${parsedDelay}ms`);
    });

    glowItems.forEach((item) => {
        if (!(item instanceof HTMLElement)) {
            return;
        }

        const glowValue = item.dataset.landingGlow;
        if (typeof glowValue !== 'string') {
            return;
        }

        const normalizedGlow = glowValue.trim();
        if (!/^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/.test(normalizedGlow)) {
            return;
        }

        item.style.setProperty('--card-glow', normalizedGlow);
    });

    const setActiveShowcaseCard = (cards, nextIndex) => {
        cards.forEach((card, index) => {
            const isActive = index === nextIndex;
            card.classList.toggle('is-active', isActive);
            card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
    };

    const supportsHoverPause = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const mobileShowcaseQuery = window.matchMedia('(max-width: 767px)');

    showcaseCardContainers.forEach((container) => {
        if (!(container instanceof HTMLElement)) {
            return;
        }

        const cards = Array.from(container.querySelectorAll('[data-landing-showcase-card]')).filter(
            (card) => card instanceof HTMLElement
        );

        if (cards.length === 0) {
            return;
        }

        const measureStackHeight = () => {
            const maxCardHeight = cards.reduce((maxHeight, card) => Math.max(maxHeight, card.offsetHeight), 0);
            if (maxCardHeight > 0) {
                container.style.height = `${maxCardHeight}px`;
            }
        };

        cards.forEach((card) => {
            const images = Array.from(card.querySelectorAll('img'));
            images.forEach((image) => {
                if (!(image instanceof HTMLImageElement) || image.complete) {
                    return;
                }

                image.addEventListener('load', measureStackHeight, { once: true });
            });
        });

        window.requestAnimationFrame(measureStackHeight);
        window.addEventListener('resize', measureStackHeight);

        let currentIndex = cards.findIndex((card) => card.classList.contains('is-active'));
        if (currentIndex < 0) {
            currentIndex = 0;
        }
        setActiveShowcaseCard(cards, currentIndex);

        if (mobileShowcaseQuery.matches) {
            setActiveShowcaseCard(cards, 0);
            return;
        }

        if (prefersReducedMotion || cards.length < 2) {
            return;
        }

        const rotationDelayMs = 6000;
        let rotationTimeout = null;
        let nextRotationDeadline = 0;
        let remainingUntilRotationMs = rotationDelayMs;
        let isRotationPaused = false;

        const rotateCard = () => {
            const nextIndex = (currentIndex + 1) % cards.length;
            setActiveShowcaseCard(cards, nextIndex);
            currentIndex = nextIndex;
        };

        const clearRotationTimeout = () => {
            if (rotationTimeout === null) {
                return;
            }

            window.clearTimeout(rotationTimeout);
            rotationTimeout = null;
        };

        const scheduleRotation = (delayMs) => {
            const normalizedDelay = Math.max(0, delayMs);

            clearRotationTimeout();
            nextRotationDeadline = Date.now() + normalizedDelay;
            rotationTimeout = window.setTimeout(() => {
                rotateCard();
                remainingUntilRotationMs = rotationDelayMs;
                scheduleRotation(rotationDelayMs);
            }, normalizedDelay);
        };

        const startRotation = () => {
            isRotationPaused = false;
            remainingUntilRotationMs = rotationDelayMs;
            scheduleRotation(rotationDelayMs);
        };

        const pauseRotation = () => {
            if (rotationTimeout === null) {
                return;
            }

            remainingUntilRotationMs = Math.max(0, nextRotationDeadline - Date.now());
            clearRotationTimeout();
            isRotationPaused = true;
        };

        const resumeRotation = () => {
            if (!isRotationPaused) {
                return;
            }

            scheduleRotation(remainingUntilRotationMs);
            isRotationPaused = false;
        };

        startRotation();

        if (!supportsHoverPause) {
            return;
        }

        container.addEventListener('mouseenter', pauseRotation);
        container.addEventListener('mouseleave', resumeRotation);
    });

    const showcaseRevealItems = (
        showcaseSection instanceof HTMLElement
            ? revealItems.filter((item) => showcaseSection.contains(item))
            : []
    );
    const showcaseRevealItemSet = new Set(showcaseRevealItems);
    const generalRevealItems = revealItems.filter((item) => !showcaseRevealItemSet.has(item));

    if (prefersReducedMotion) {
        revealItems.forEach((item) => {
            if (item instanceof HTMLElement) {
                item.classList.add('is-visible');
            }
        });
    } else {
        const revealObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                if (entry.target instanceof HTMLElement) {
                    entry.target.classList.add('is-visible');
                }
                revealObserver.unobserve(entry.target);
            });
        }, {
            threshold: 0.2,
            rootMargin: '0px 0px -8% 0px',
        });

        generalRevealItems.forEach((item) => {
            revealObserver.observe(item);
        });

        if (showcaseSection instanceof HTMLElement && showcaseRevealItems.length > 0) {
            const showcaseObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    showcaseRevealItems.forEach((item) => {
                        if (item instanceof HTMLElement) {
                            item.classList.add('is-visible');
                        }
                    });

                    showcaseObserver.unobserve(entry.target);
                });
            }, {
                threshold: 0.2,
                rootMargin: '0px 0px -8% 0px',
            });

            showcaseObserver.observe(showcaseSection);
        }
    }

    let isTransitioning = false;
    // Long enough for the click to read as a response, never a wait: the cover reveal (700ms) is not
    // awaited and keeps playing while the next page loads.
    const navigationDelayMs = 225;

    // Back restores this page from bfcache mid-transition: hidden hero, revealed covers, dead CTA.
    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) {
            return;
        }

        isTransitioning = false;
        heroContent?.classList.remove('hero-content--exit');
        ghostDashboard?.classList.remove('landing-ghost-dashboard--reveal');
    });

    ctaButtons.forEach((cta) => {
        if (!(cta instanceof HTMLAnchorElement)) {
            return;
        }

        cta.addEventListener('click', (event) => {
            if (
                event.defaultPrevented
                || event.button !== 0
                || event.metaKey
                || event.ctrlKey
                || event.shiftKey
                || event.altKey
            ) {
                return;
            }

            const destination = cta.getAttribute('href');
            if (typeof destination !== 'string' || destination.trim() === '') {
                return;
            }

            event.preventDefault();

            if (isTransitioning) {
                return;
            }

            if (prefersReducedMotion) {
                window.location.assign(destination);

                return;
            }

            isTransitioning = true;

            if (heroContent instanceof HTMLElement) {
                heroContent.classList.add('hero-content--exit');
            }

            if (ghostDashboard instanceof HTMLElement) {
                ghostDashboard.classList.add('landing-ghost-dashboard--reveal');
            }

            window.setTimeout(() => {
                window.location.assign(destination);
            }, navigationDelayMs);
        });
    });

    if (!(hero instanceof HTMLElement)) {
        return;
    }

    const supportsPrecisePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const cursorGlowEnabled = supportsPrecisePointer && window.matchMedia('(min-width: 768px)').matches && !prefersReducedMotion;
    const parallaxEnabled = supportsPrecisePointer && window.matchMedia('(min-width: 1024px)').matches && !prefersReducedMotion;
    const cursorGlowRadiusPx = 250;
    const cursorGlowLerpFactor = 0.15;
    let cursorGlowTargetX = 0;
    let cursorGlowTargetY = 0;
    let cursorGlowCurrentX = 0;
    let cursorGlowCurrentY = 0;

    if (cursorGlowEnabled) {
        hero.classList.add('has-cursor-glow');

        const initialRect = hero.getBoundingClientRect();
        cursorGlowTargetX = initialRect.width / 2;
        cursorGlowTargetY = initialRect.height / 2;
        cursorGlowCurrentX = cursorGlowTargetX;
        cursorGlowCurrentY = cursorGlowTargetY;
        hero.style.setProperty(
            '--landing-cursor-glow-transform',
            `translate3d(${(cursorGlowCurrentX - cursorGlowRadiusPx).toFixed(2)}px, ${(cursorGlowCurrentY - cursorGlowRadiusPx).toFixed(2)}px, 0)`
        );

        const animateCursorGlow = () => {
            cursorGlowCurrentX += (cursorGlowTargetX - cursorGlowCurrentX) * cursorGlowLerpFactor;
            cursorGlowCurrentY += (cursorGlowTargetY - cursorGlowCurrentY) * cursorGlowLerpFactor;

            hero.style.setProperty(
                '--landing-cursor-glow-transform',
                `translate3d(${(cursorGlowCurrentX - cursorGlowRadiusPx).toFixed(2)}px, ${(cursorGlowCurrentY - cursorGlowRadiusPx).toFixed(2)}px, 0)`
            );

            window.requestAnimationFrame(animateCursorGlow);
        };

        window.requestAnimationFrame(animateCursorGlow);
    }

    if (!cursorGlowEnabled && !parallaxEnabled) {
        return;
    }

    const updateFromPointer = (event) => {
        const rect = hero.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return;
        }

        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;

        if (cursorGlowEnabled) {
            cursorGlowTargetX = offsetX;
            cursorGlowTargetY = offsetY;
        }

        if (parallaxEnabled && ghostShell instanceof HTMLElement) {
            const normalizedX = ((offsetX / rect.width) - 0.5) * 2;
            const normalizedY = ((offsetY / rect.height) - 0.5) * 2;
            const clampedX = Math.max(-1, Math.min(1, normalizedX));
            const clampedY = Math.max(-1, Math.min(1, normalizedY));

            ghostShell.style.setProperty('--landing-parallax-x', `${(-clampedX * 10).toFixed(2)}px`);
            ghostShell.style.setProperty('--landing-parallax-y', `${(-clampedY * 10).toFixed(2)}px`);
        }
    };

    hero.addEventListener('mousemove', updateFromPointer);
    hero.addEventListener('mouseleave', () => {
        if (cursorGlowEnabled) {
            const rect = hero.getBoundingClientRect();
            cursorGlowTargetX = rect.width / 2;
            cursorGlowTargetY = rect.height / 2;
        }

        if (parallaxEnabled && ghostShell instanceof HTMLElement) {
            ghostShell.style.setProperty('--landing-parallax-x', '0px');
            ghostShell.style.setProperty('--landing-parallax-y', '0px');
        }
    });
})();
